/**
 * TeamSettingsDashboard Component
 *
 * Allows Area Managers and HQ Managers to:
 * - View settings for all team members
 * - See who has customizations vs using org defaults
 * - Access detailed settings for each team member
 * - Apply templates to team members
 * - View analytics on settings usage
 *
 * Date: 2026-01-11
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Users,
    TrendingUp,
    ChevronDown,
    ChevronUp,
    Filter,
    Search,
    X,
    Check,
    AlertCircle,
    Copy
} from 'lucide-react';
import API_BASE_URL from '../apiConfig';
import TemplateManager from './TemplateManager';
import SettingsAnalytics from './SettingsAnalytics';

const TeamSettingsDashboard = ({ currentUser, onClose }) => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterCustomization, setFilterCustomization] = useState('all');
    const [expandedMember, setExpandedMember] = useState(null);
    const [activeTab, setActiveTab] = useState('team'); // 'team', 'templates', 'analytics'
    const [selectedMembers, setSelectedMembers] = useState(new Set());

    useEffect(() => {
        loadTeamSettings();
    }, []);

    const loadTeamSettings = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(
                `${API_BASE_URL}/api/manager/team-settings`
            );

            setTeamMembers(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error loading team settings:', err);
            setError(err.response?.data?.detail || 'Failed to load team settings');
            setLoading(false);
        }
    };

    const toggleMemberExpansion = (userId) => {
        setExpandedMember(expandedMember === userId ? null : userId);
    };

    const toggleMemberSelection = (userId) => {
        const newSelected = new Set(selectedMembers);
        if (newSelected.has(userId)) {
            newSelected.delete(userId);
        } else {
            newSelected.add(userId);
        }
        setSelectedMembers(newSelected);
    };

    const selectAll = () => {
        const filtered = getFilteredMembers();
        setSelectedMembers(new Set(filtered.map(m => m.user_id)));
    };

    const deselectAll = () => {
        setSelectedMembers(new Set());
    };

    const getFilteredMembers = () => {
        return teamMembers.filter(member => {
            // Search filter
            const matchesSearch = searchTerm === '' ||
                member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.email.toLowerCase().includes(searchTerm.toLowerCase());

            // Role filter
            const matchesRole = filterRole === 'all' || member.role === filterRole;

            // Customization filter
            const matchesCustomization =
                filterCustomization === 'all' ||
                (filterCustomization === 'customized' && member.has_customizations) ||
                (filterCustomization === 'default' && !member.has_customizations);

            return matchesSearch && matchesRole && matchesCustomization;
        });
    };

    const filteredMembers = getFilteredMembers();

    const stats = {
        total: teamMembers.length,
        withCustomizations: teamMembers.filter(m => m.has_customizations).length,
        usingDefaults: teamMembers.filter(m => !m.has_customizations).length,
        customizationRate: teamMembers.length > 0
            ? (teamMembers.filter(m => m.has_customizations).length / teamMembers.length * 100).toFixed(0)
            : 0
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                    <p className="text-center mt-4 text-gray-600">Loading team settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <Users className="w-6 h-6 text-blue-600" />
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Team Settings</h2>
                            <p className="text-sm text-gray-500">
                                Manage settings for {stats.total} team member{stats.total !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-red-800">Error</h3>
                            <p className="text-sm text-red-700 mt-1">{error}</p>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-400 hover:text-red-600 ml-4"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex border-b border-gray-200 px-6">
                    <button
                        onClick={() => setActiveTab('team')}
                        className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${activeTab === 'team'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Users className="w-4 h-4 inline mr-2" />
                        Team Members
                    </button>
                    <button
                        onClick={() => setActiveTab('templates')}
                        className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${activeTab === 'templates'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Copy className="w-4 h-4 inline mr-2" />
                        Templates
                    </button>
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${activeTab === 'analytics'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <TrendingUp className="w-4 h-4 inline mr-2" />
                        Analytics
                    </button>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-hidden">
                    {activeTab === 'team' && (
                        <TeamMembersTab
                            members={filteredMembers}
                            stats={stats}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            filterRole={filterRole}
                            setFilterRole={setFilterRole}
                            filterCustomization={filterCustomization}
                            setFilterCustomization={setFilterCustomization}
                            expandedMember={expandedMember}
                            toggleMemberExpansion={toggleMemberExpansion}
                            selectedMembers={selectedMembers}
                            toggleMemberSelection={toggleMemberSelection}
                            selectAll={selectAll}
                            deselectAll={deselectAll}
                            onRefresh={loadTeamSettings}
                        />
                    )}

                    {activeTab === 'templates' && (
                        <TemplateManager
                            currentUser={currentUser}
                            teamMembers={teamMembers}
                            selectedMembers={selectedMembers}
                            onTemplateApplied={loadTeamSettings}
                        />
                    )}

                    {activeTab === 'analytics' && (
                        <SettingsAnalytics currentUser={currentUser} />
                    )}
                </div>
            </div>
        </div>
    );
};

// ==========================================
// TEAM MEMBERS TAB
// ==========================================

const TeamMembersTab = ({
    members,
    stats,
    searchTerm,
    setSearchTerm,
    filterRole,
    setFilterRole,
    filterCustomization,
    setFilterCustomization,
    expandedMember,
    toggleMemberExpansion,
    selectedMembers,
    toggleMemberSelection,
    selectAll,
    deselectAll,
    onRefresh
}) => {
    return (
        <div className="flex flex-col h-full">
            {/* Stats Bar */}
            <div className="grid grid-cols-4 gap-4 p-6 bg-gray-50 border-b border-gray-200">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                    <div className="text-sm text-gray-500">Total Members</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-green-600">{stats.withCustomizations}</div>
                    <div className="text-sm text-gray-500">With Customizations</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-gray-400">{stats.usingDefaults}</div>
                    <div className="text-sm text-gray-500">Using Defaults</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-blue-600">{stats.customizationRate}%</div>
                    <div className="text-sm text-gray-500">Customization Rate</div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="p-6 border-b border-gray-200 space-y-4">
                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Filters */}
                <div className="flex items-center space-x-4">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="all">All Roles</option>
                        <option value="salesperson">Salespeople</option>
                        <option value="area_manager">Area Managers</option>
                        <option value="hq_manager">HQ Managers</option>
                    </select>

                    <select
                        value={filterCustomization}
                        onChange={(e) => setFilterCustomization(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="all">All Settings</option>
                        <option value="customized">Has Customizations</option>
                        <option value="default">Using Defaults</option>
                    </select>

                    {/* Bulk Selection */}
                    {selectedMembers.size > 0 && (
                        <div className="ml-auto flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                                {selectedMembers.size} selected
                            </span>
                            <button
                                onClick={deselectAll}
                                className="text-sm text-blue-600 hover:text-blue-700"
                            >
                                Clear
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Team Members List */}
            <div className="flex-1 overflow-y-auto p-6">
                {members.length === 0 ? (
                    <div className="text-center py-12">
                        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No team members match your filters</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {members.map(member => (
                            <TeamMemberCard
                                key={member.user_id}
                                member={member}
                                isExpanded={expandedMember === member.user_id}
                                onToggleExpand={() => toggleMemberExpansion(member.user_id)}
                                isSelected={selectedMembers.has(member.user_id)}
                                onToggleSelect={() => toggleMemberSelection(member.user_id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==========================================
// TEAM MEMBER CARD
// ==========================================

const TeamMemberCard = ({ member, isExpanded, onToggleExpand, isSelected, onToggleSelect }) => {
    const roleColors = {
        salesperson: 'bg-green-100 text-green-800',
        area_manager: 'bg-blue-100 text-blue-800',
        hq_manager: 'bg-purple-100 text-purple-800'
    };

    const roleLabels = {
        salesperson: 'Salesperson',
        area_manager: 'Area Manager',
        hq_manager: 'HQ Manager'
    };

    return (
        <div className={`border rounded-lg transition-all ${member.has_customizations
                ? 'border-green-300 bg-green-50'
                : 'border-gray-200 bg-white'
            }`}>
            {/* Card Header */}
            <div className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                    {/* Selection Checkbox */}
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={onToggleSelect}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />

                    {/* Member Info */}
                    <div className="flex-1">
                        <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">{member.name}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[member.role]}`}>
                                {roleLabels[member.role]}
                            </span>
                            {member.has_customizations && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <Check className="w-3 h-3 inline mr-1" />
                                    Customized
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{member.email}</p>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-6">
                        <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">{member.override_count}</div>
                            <div className="text-xs text-gray-500">Overrides</div>
                        </div>
                        {member.last_modified_at && (
                            <div className="text-center">
                                <div className="text-sm text-gray-600">
                                    {new Date(member.last_modified_at).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-500">Last Modified</div>
                            </div>
                        )}
                    </div>

                    {/* Expand Button */}
                    {member.has_customizations && (
                        <button
                            onClick={onToggleExpand}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-gray-600" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-gray-600" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && member.has_customizations && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-4 bg-white">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Customized Settings:</h4>
                    <div className="grid grid-cols-2 gap-4">
                        {Object.entries(member.overridden_settings).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Custom Value</div>
                                </div>
                                <div className="text-sm font-bold text-green-600">{value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamSettingsDashboard;
